import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

export function EmptyState({ icon: Icon, title, text, action, testId }: { icon: any; title: string; text?: string; action?: React.ReactNode; testId: string }) {
  return (
    <div data-testid={testId} className="flex flex-col items-center text-center py-12 px-6 rounded-2xl border border-dashed border-slate-300 bg-white/60">
      <div className="h-14 w-14 rounded-2xl bg-emerald-50 text-emerald-700 grid place-items-center mb-4">
        <Icon className="h-7 w-7" />
      </div>
      <p className="font-semibold text-slate-800">{title}</p>
      {text && <p className="text-sm text-slate-500 mt-1 max-w-xs">{text}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div data-testid="loading-skeleton" className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-20 w-full rounded-2xl" />
      ))}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useT();
  return (
    <div data-testid="error-state" className="rounded-2xl border border-rose-200 bg-rose-50 p-5 text-rose-700">
      <p className="text-sm">{message}</p>
      <Button data-testid="error-retry-btn" variant="outline" className="mt-3 h-11" onClick={onRetry}>{t("retry")}</Button>
    </div>
  );
}

export function PageTitle({ title, sub, right }: { title: string; sub?: string; right?: React.ReactNode }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-5 pg-rise">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
        {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
      </div>
      {right}
    </div>
  );
}

export function Card({ children, className = "", ...rest }: any) {
  return <div className={cn("rounded-2xl bg-white border border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)]", className)} {...rest}>{children}</div>;
}
